/**
 * Mobile Header - Hamburger Navigation
 *
 * This component renders a mobile-only header bar with a hamburger menu button.
 * It is only visible on mobile devices (hidden on md and larger screens).
 *
 * Features:
 * - Hamburger menu icon that toggles the mobile sidebar overlay
 * - Application logo/title
 * - V17.8: Notification bell with real-time updates
 * - V17.59.4: Dark mode toggle (Sun/Moon icon)
 * - User profile indicator
 * - V17.518: the avatar opens an account menu (name, language, Log out; V17.539 moved its role switcher to the drawer) — on a
 *   phone Layout hides the desktop Header, which held the only logout
 * - Responsive visibility (mobile only)
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, LogOut, Menu, Moon, Sun } from 'lucide-react';
import { useLayout } from '../../contexts/LayoutContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { NotificationBell } from '../NotificationBell';

export default function MobileHeader() {
  const { toggleMobileSidebar } = useLayout();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  // V17.506 (row 72 (b), judge finding from (a)): Lao screen-reader names on Lao screens.
  const { language, toggleLanguage } = useLanguage();
  const lo = language === 'lo';
  const navigate = useNavigate();
  const [isAccountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);

  // V17.518: close the account menu on Escape or a tap outside it.
  useEffect(() => {
    if (!isAccountOpen) return;
    const onOutside = (event: MouseEvent | TouchEvent) => {
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) setAccountOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAccountOpen(false);
        avatarRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [isAccountOpen]);

  // Same as Header.handleLogout.
  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate('/login');
  };

  return (
    <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-30 px-4 flex items-center justify-between">
      {/* Hamburger Menu Button */}
      <button
        onClick={toggleMobileSidebar}
        // Brief 2026-10-27-001 (SC-6, audit F15): ≥ 44×44 tap area (was 40×40).
        className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label={lo ? 'ເປີດ / ປິດເມນູ' : 'Toggle navigation menu'}
      >
        <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
      </button>

      {/* Application Logo/Title */}
      <div className="flex items-center">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">VTW Hub</h1>
      </div>

      {/* V17.8: Notification Bell, Dark Mode Toggle, and User Profile */}
      <div className="flex items-center gap-2">
        {/* V17.59.4: Dark Mode Toggle */}
        <button
          onClick={toggleTheme}
          // SC-6: ≥ 44×44 tap area (was 36×36).
          className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title={
            theme === 'dark'
              ? lo ? 'ປ່ຽນເປັນໂໝດສະຫວ່າງ' : 'Switch to Light Mode'
              : lo ? 'ປ່ຽນເປັນໂໝດມືດ' : 'Switch to Dark Mode'
          }
          aria-label={
            theme === 'dark'
              ? lo ? 'ປ່ຽນເປັນໂໝດສະຫວ່າງ' : 'Switch to Light Mode'
              : lo ? 'ປ່ຽນເປັນໂໝດມືດ' : 'Switch to Dark Mode'
          }
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        {/* Notification Bell - visible for all authenticated users */}
        {user && <NotificationBell />}

        {/* V17.518: User avatar opens the account menu (the phone's only logout) */}
        {user && (
          <div className="relative" ref={accountRef}>
            <button
              ref={avatarRef}
              type="button"
              onClick={() => setAccountOpen((open) => !open)}
              className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={lo ? 'ບັນຊີຂອງຂ້ອຍ' : 'My account'}
              aria-haspopup="menu"
              aria-expanded={isAccountOpen}
            >
              <span className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                {user.username.charAt(0).toUpperCase()}
              </span>
            </button>

            {isAccountOpen && (
              <div
                role="menu"
                aria-label={lo ? 'ບັນຊີຂອງຂ້ອຍ' : 'My account'}
                className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
              >
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.full_name}</p>
                  {/* V17.539 (2C): the role control moved to the top of the menu drawer. */}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={toggleLanguage}
                  className="w-full min-h-[44px] flex items-center gap-3 px-4 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Globe className="h-5 w-5" />
                  <span>{lo ? 'English' : 'ພາສາລາວ'}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full min-h-[44px] flex items-center gap-3 px-4 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  <LogOut className="h-5 w-5" />
                  <span>{lo ? 'ອອກຈາກລະບົບ' : 'Log out'}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
